import { MonitorSmartphone } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { authApi } from '../../api/resources/auth';
import { useSession } from '../../app/session';
import { useLocale } from '../../lib/useLocale';

const STORAGE_KEY = 'mdk_game_ticket';

function looksLikeTicket(value: string | null): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9_-]{32,128}$/.test(value);
}

function readStoredTicket(): string | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return looksLikeTicket(raw) ? raw : null;
  } catch {
    return null;
  }
}

function writeStoredTicket(ticket: string | null) {
  try {
    if (ticket) sessionStorage.setItem(STORAGE_KEY, ticket);
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* private mode */
  }
}

/**
 * In-game WebBrowser SSO: capture `game_ticket` from the URL, and once the
 * player has a mgr_session, bind it. Tokens stay on the server until the game
 * HMAC-claims them. `from=game` is display-only and never trusted.
 */
export function GameTicketSync() {
  const { t } = useLocale();
  const { isAuthenticated, isRestoring } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState<'idle' | 'waiting' | 'syncing' | 'bound' | 'error'>(() =>
    readStoredTicket() ? 'waiting' : 'idle',
  );
  const bindOnce = useRef(false);

  useEffect(() => {
    const fromQuery = looksLikeTicket(searchParams.get('game_ticket'))
      ? searchParams.get('game_ticket')
      : null;
    if (!fromQuery) return;
    writeStoredTicket(fromQuery);
    setStatus((prev) => (prev === 'bound' ? prev : 'waiting'));
    const next = new URLSearchParams(searchParams);
    next.delete('game_ticket');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (isRestoring || !isAuthenticated) return;
    const ticket = readStoredTicket();
    if (!ticket || bindOnce.current) return;
    bindOnce.current = true;
    setStatus('syncing');
    void authApi
      .bindGameTicket(ticket)
      .then(() => {
        writeStoredTicket(null);
        setStatus('bound');
      })
      .catch(() => {
        bindOnce.current = false;
        setStatus('error');
      });
  }, [isAuthenticated, isRestoring]);

  if (status === 'idle') return null;

  const copy =
    status === 'bound'
      ? t('auth.gameTicketBound')
      : status === 'error'
        ? t('auth.gameTicketError')
        : t('auth.gameTicketPending');

  return (
    <div
      className="border-b border-primary/20 bg-primary-soft px-4 py-2 text-center text-xs text-primary sm:px-6"
      role="status"
    >
      <span className="mx-auto flex w-full max-w-4xl items-center justify-center gap-2">
        <MonitorSmartphone className="size-3.5 shrink-0" aria-hidden="true" />
        <span>{copy}</span>
      </span>
    </div>
  );
}
